/**
 * Template Name: Count
 *
 * "1,234 Courses" — the plainest stats line, and the default.
 *
 * THE THREE SHIPPED TEMPLATES
 *
 * Count (this file), Range ("Showing 1 - 20 of 1,234 Courses") and Page ("Page 3 of 62") differ
 * only in the SHAPE of the sentence. The words come from the view, and the two optional extras —
 * the search query and the processing time — are toggles on the widget rather than templates of
 * their own. That is deliberate: shape × query × time is twelve combinations, and a picker listing
 * twelve entries makes the reader compare suffixes instead of choosing a shape.
 *
 * WHERE THE VALUES COME FROM
 *
 *   View editor → "View result singular label" / "View result plural label"
 *     → stored on the view (view_result_singular_label / view_result_plural_label)
 *     → merged into the widget's settings by MetadataHandler
 *     → sent to the browser by StatsShortcodeGenerator as `stats_labels`
 *     → handed to this function as the `labels` binding by searchsync-stats-component.js
 *
 *   Meta Data tab → "Show search query" / "Show processing time"
 *     → stored on the widget (stats_show_query / stats_show_time)
 *     → sent as `stats_options` → handed over as the `options` binding
 *
 * WHAT THIS FUNCTION RECEIVES
 *
 *   labels.singular      string  e.g. "Course"   — empty when the view hasn't set one
 *   labels.plural        string  e.g. "Courses"  — empty when the view hasn't set one
 *   labels.nothingFound  string  the view's "Nothing Found" message; replaces the whole line when
 *                                set, since the empty state is where a site most often wants its
 *                                own sentence rather than a count
 *   options.showQuery    bool    append ` for "<what they typed>"`
 *   options.showTime     bool    append ` (12ms)`
 *   data.*           the InstantSearch stats payload (nbHits, hitsPerPage, page, nbPages,
 *                    query, processingTimeMS, hasNoResults, hasOneResult, areHitsSorted, …)
 *   html, __, _n, sprintf   rendering + translation helpers
 *
 * COPYING THIS INTO A THEME
 *
 * Drop a copy in <your-theme>/searchsync/stats-templates/ and it appears in the picker under
 * "My Templates". Change `D` to your theme's text domain so the strings belong to the site. A
 * theme template that has no use for the extras simply omits `supports` below, and the two
 * toggles are then hidden rather than sitting there doing nothing.
 */

/**
 * Which of the widget's optional extras this template actually renders. The editor reads this off
 * the module it already imports for the preview, and only offers the toggles listed here.
 */
export const supports = [ 'query', 'time' ];

export function renderStatsTemplate( data, { html, __, _n, sprintf, labels, options } ) {
  // Plugin-shipped template — strings use the plugin text domain.
  // (A theme's own template should pass ITS domain instead, e.g. 'my-theme'.)
  const D = 'tangible-searchsync';

  // Algolia's relevant sort reports its own counts. When it is active, nbHits/hasNoResults
  // describe the unsorted set and would contradict the hits actually on the page.
  const total     = data.areHitsSorted ? data.nbSortedHits       : data.nbHits;
  const noResults = data.areHitsSorted ? data.hasNoSortedResults : data.hasNoResults;
  const oneResult = data.areHitsSorted ? data.hasOneSortedResults : data.hasOneResult;

  // The view's words for what it lists, or generic wording when it never set them — so this
  // template is safe to select on any view rather than only on configured ones.
  //
  // gettext cannot pluralise a site owner's own noun, so when labels ARE set the two of them are
  // the plural forms and _n() governs only the fallback. A view that filled in just one of the two
  // uses it for both rather than mixing its word with ours.
  const items = ( n ) => {
    if ( labels?.singular || labels?.plural ) {
      return n === 1
        ? ( labels.singular || labels.plural )
        : ( labels.plural || labels.singular );
    }
    return _n( 'result', 'results', n, D );
  };

  // The optional extras, appended in a fixed order so the line reads the same everywhere. Each is
  // skipped when the underlying value is absent: `showQuery` on a page nobody has typed into would
  // otherwise render a dangling `for ""`.
  const withExtras = ( line ) => {
    let out = line;

    if ( options?.showQuery && data.query ) {
      /* translators: 1: the stats line so far, 2: the visitor's search term. */
      out = sprintf( __( '%1$s for "%2$s"', D ), out, data.query );
    }

    if ( options?.showTime && typeof data.processingTimeMS === 'number' ) {
      /* translators: 1: the stats line so far, 2: search time in milliseconds. */
      out = sprintf( __( '%1$s (%2$sms)', D ), out, data.processingTimeMS );
    }

    return out;
  };

  if ( noResults ) {
    // The view's "Nothing Found" message replaces this line entirely when set, rather than being
    // interpolated into it: the empty state is the one case where a site usually wants its own
    // sentence ("We couldn't find anything matching that") instead of a count-shaped one. Not
    // translated here — it is the site owner's own text, already in their language. The extras are
    // skipped with it, since that sentence is written to stand alone.
    if ( labels?.nothingFound ) {
      return html`<span>${ labels.nothingFound }</span>`;
    }

    /* translators: %s: plural item label, e.g. "Courses". */
    return html`<span>${ withExtras( sprintf( __( 'No %s found', D ), items( 0 ) ) ) }</span>`;
  }

  if ( oneResult ) {
    /* translators: %s: singular item label, e.g. "Course". */
    return html`<span>${ withExtras( sprintf( __( '1 %s', D ), items( 1 ) ) ) }</span>`;
  }

  /* translators: 1: formatted result count, 2: plural item label, e.g. "Courses". */
  return html`<span>${ withExtras( sprintf(
    __( '%1$s %2$s', D ),
    total.toLocaleString(),
    items( total )
  ) ) }</span>`;
}
